import React from 'react';

import { uniqBy } from 'lodash';
import { Member } from '../Member';
import { AddMember } from './AddMember';
import { useGroupsSidebarDispatch, useGroupsSidebarState } from 'context';
import { Loader, Toast } from 'components';

import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import { Logger } from 'services';
import { addGroupMemberOptimisticMutation } from 'services/apollo/mutations';
import { useClickOutside, useTranslation } from 'hooks';
import {
  trackingGroupAddMemberComplete,
  trackingGroupAddMemberClick,
  trackingGroupAddMemberSearch,
} from 'services/analytics/interceptor/handlers/trackGroup';
import produce from 'immer';
import { GroupListQuery } from 'services';
import { toast } from 'react-toastify';

interface AddMembersProps {
  /** @group Group to add members to */
  group?: Group;
  /** @number Number of group members */
  memberCount?: number;
  /** @boolean If the group heading should render with an alternate style */
  alternateStyle?: boolean;
}

/**
 * Handles the slot components creation
 * @param memberCount
 */
const createEmptySlots = (memberCount: number) => {
  let slots;
  let slotsComponents = [];

  if (memberCount === 0) {
    slots = 3;
  } else if (memberCount === 1) {
    slots = 2;
  } else {
    slots = 1;
  }

  for (let i = 0; i < slots; i++) {
    slotsComponents.push(i);
  }

  return slotsComponents;
};

export const AddMembers: React.FC<AddMembersProps> = ({ group, memberCount = 0, alternateStyle }) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [query, setQuery] = React.useState('');
  const [isAdding, setIsAdding] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);

  const [addMemberToGroup] = useMutation(addGroupMemberOptimisticMutation, {
    update(cache, { data }) {
      const queryResult: any = cache.readQuery({ query: GroupListQuery });
      if (data?.addGroupMember?.error) {
        toast(<Toast message={t('toasts.add-to-group-error')} hasCloseButton />, {
          position: toast.POSITION.BOTTOM_LEFT,
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: true,
          autoClose: 5000,
        });
        try {
          cache.writeQuery({
            query: GroupListQuery,
            data: produce(queryResult, (draft: any) => {
              const groups = [...draft.me.groups];
              const matchedGroup = groups.find((searchGroup) => searchGroup.id === group!.id);
              if (matchedGroup && matchedGroup.members) {
                matchedGroup.members.pop();
              }
            }),
          });
        } catch (err) {
          Logger.log(err);
        }
      }
    },
  });

  const sidebarState = useGroupsSidebarState();
  const sidebarDispatch = useGroupsSidebarDispatch();
  const slotComponents = React.useMemo(() => createEmptySlots(memberCount), [memberCount]);

  const { error, data } = useQuery(GroupListQuery, {
    fetchPolicy: 'cache-first',
  });

  // Merges, removes contacts without email and dedupes otherPeople and topContacts
  const contacts = React.useMemo(() => {
    const mergedContacts = [
      ...(data?.me?.orgGroup?.members ?? []),
      ...(data?.me?.userProfile?.topContacts?.contacts ?? []),
      ...(data?.me?.otherPeople ?? []),
    ];
    const contactsWithoutEmail = mergedContacts.filter((contact: User) => !!contact.email);
    return uniqBy(contactsWithoutEmail, 'email');
  }, [data]);

  const { createdGroupId } = sidebarState;

  // Autofocus input if group was just created
  React.useEffect(() => {
    if (createdGroupId === group?.id) {
      setIsAdding(true);
      sidebarDispatch({ type: 'CREATE_GROUP', payload: '' });
    }
  }, [group, sidebarDispatch, createdGroupId]);

  /**
   * Finds the contacts that have matching name or email
   * Excludes members already in the group
   * @param query
   */
  const filterContactsByQuery = React.useCallback(
    (query: string) => {
      return contacts.filter((contact: User) => {
        const searchQuery = query.toLocaleLowerCase();
        const isAlreadyMember = !!(group!.members || []).find((groupMember: User) => {
          return groupMember.email === contact.email;
        });

        if (isAlreadyMember) {
          return false;
        }

        const name = contact.name ? contact.name.toLowerCase() : '';
        const email = contact.email || '';

        return name.includes(searchQuery) || email.includes(searchQuery);
      });
    },
    [contacts, group]
  );

  /**
   * Called when the icon plus circle is clicked
   * and the user is ready to start searching for a member
   */
  const startMemberAdding = React.useCallback(() => {
    trackingGroupAddMemberClick({
      name: group?.name || '',
      type: group?.groupType,
    });

    if (!isAdding) {
      setIsAdding(true);
    }
  }, [group, isAdding]);

  /**
   * Cancels member adding. Reverts to initial state.
   */
  const cancelMemberAdding = React.useCallback(() => {
    if (isAdding || isSearching) {
      setQuery('');
      setIsAdding(false);
      setIsSearching(false);
      sidebarDispatch({ type: 'CANCEL_GROUP_SEARCH' });
    }
  }, [isAdding, isSearching, sidebarDispatch]);

  /**
   * Called everytime the member input search changes
   * @param value
   */
  const startMemberSearch = React.useCallback(
    (value: string) => {
      setQuery(value);

      if (value === '') {
        cancelMemberAdding();
        return;
      }

      if (!isSearching) {
        sidebarDispatch({ type: 'START_GROUP_SEARCH', payload: group!.id });
        setIsSearching(true);
        trackingGroupAddMemberSearch({
          name: group?.name || '',
          type: group?.groupType,
        });
      }
    },
    [group, isSearching, cancelMemberAdding, sidebarDispatch]
  );

  /**
   * Adds a member to a group
   * @param personId
   */
  const addMember = React.useCallback(
    (person: User) => {
      cancelMemberAdding();

      const queryResult: any = client.readQuery({ query: GroupListQuery });

      const newData = produce(queryResult, (draft: any) => {
        const groups = [...draft.me.groups] || [];
        const matchedGroup = groups.find((groupFind) => groupFind.id === group!.id);

        if (matchedGroup && matchedGroup.members) {
          matchedGroup?.members.push({
            id: person.id,
            name: person.name,
            email: person.email,
            avatarUrl: person.avatarUrl,
            __typename: 'GroupMember',
          });
        }
      });

      try {
        client.writeQuery({
          query: GroupListQuery,
          data: newData,
        });
      } catch (err) {
        Logger.log(err);
      }

      addMemberToGroup({
        variables: {
          groupId: group!.id,
          personId: person.id,
        },
      }).then(() => {
        trackingGroupAddMemberComplete({
          name: group?.name || '',
          type: group?.groupType,
          member: person.email || person.id!,
        });
      });
    },
    [client, group, addMemberToGroup, cancelMemberAdding]
  );

  const wrapperElement: React.MutableRefObject<HTMLDivElement | null> = React.createRef();

  useClickOutside(wrapperElement, cancelMemberAdding);

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  return (
    <div ref={wrapperElement}>
      {slotComponents.map((memberSlot: number) => (
        <AddMember
          key={memberSlot}
          query={query}
          active={isAdding && memberSlot === 0}
          hidden={isSearching && memberSlot !== 0}
          onBlur={cancelMemberAdding}
          onFocus={startMemberAdding}
          onChange={startMemberSearch}
          alternateStyle={alternateStyle}
        />
      ))}

      {isSearching && (
        <>
          {filterContactsByQuery(query).map((person: User, key) => (
            <Member 
              key={key} 
              collaborator={person} 
              onClick={addMember} 
              alternateStyle={alternateStyle} 
            />
          ))}
        </>
      )}
    </div>
  );
};

export default AddMembers;
